/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {DLSTheme} from '@ion/dls/src/types/types';
import AccountTooltip from './AccountTooltip';
import useSupportedAutojobFileTypes from './useSupportedAutojobFileTypes';

export type AutojobsTooltipProps = {
  enabled: boolean;
};

export const useStyles = makeStyles((theme: DLSTheme) => ({
  autojobsTooltip : {
    textAlign : 'left',
    padding   : '0px 8px'
  },
  autojobsFileTypes : {
    textAlign   : 'left',
    paddingLeft : '16px'
  }
}));

/**
 * @AutojobsTooltip
 * 
 * A tooltip for autojobs that displays the supported file types.
 * 
 * ```typescript
 * import {AutojobsTooltip} from './AutojobsTooltip';
 * 
 * <AutojobsTooltip enabled={true} />
 * 
 * ```
 * 
 * @param   {boolean} enabled - boolean to enable the hook to get the supported autojob file types.
 * @returns {ReactElement} - The tooltip for autojobs.
 */
const AutojobsTooltip = ({enabled}: AutojobsTooltipProps) => {
  const classes                    = useStyles();
  const supportedAutojobsFileTypes = useSupportedAutojobFileTypes({enabled});

  return (
    <AccountTooltip title={
      <div className={classes.autojobsTooltip}>
        <p>{translate('admin-groups:dialog.tooltips.autojobsToolTip')}</p>
        <ul className={classes.autojobsFileTypes}>
          {supportedAutojobsFileTypes?.map?.((fileType, index) => {
            return (
              <li key={`${fileType}_${index}`}>
                {fileType}
              </li>
            );
          })}
        </ul>
        <p>{translate('admin-groups:dialog.tooltips.autojobsExplanation')}</p>
      </div>
    } align />
  );
};

export default AutojobsTooltip;
